/**
 * `resolve-party` — FR-09, FR-10, FR-14, DR-04, DR-05, IR-02, IR-03.
 *
 * Resolves a party from Qfly (Client, Partner) or Dynamics (Vendor) so the rest
 * of the system can *reference* it rather than copy it (DR-04). Two callers:
 *
 *  1. the guided upload form, which needs a party the user can only **select**
 *     (FR-14) — see `upload.ts`;
 *  2. the pseudonymisation stage (§4A), which needs the canonical name plus every
 *     confirmed alias in order to substitute deterministically.
 *
 * The second is why this endpoint returns aliases. FR-29 is deterministic rather
 * than inferential precisely because the party is known from intake metadata
 * before any reading happens (§4A.1) — no entity recognition is involved, so the
 * quality of the alias list *is* the quality of the control.
 *
 * Read-only. The write side is `manage-party-alias` — see the note at the foot
 * of this file.
 */
import { z } from 'zod';
import { ContractKeySchema, IsoTimestampUtcSchema, PartyKeySchema } from '../primitives.js';
import { PartyTypeSchema, SorSystemSchema } from '../vocabulary.js';
import { PageInfoSchema, PageRequestSchema } from '../pagination.js';
import { PartyAliasSchema, PartyAliasStateSchema, PartySchema } from '../domain/party.js';
import { VendorReferenceSchema } from '../domain/vendor-reference.js';
export const ResolvePartyRequestSchema = z
    .object({
    /** Exact lookup by key. Mutually exclusive with `search`, in practice. */
    party_key: PartyKeySchema.optional(),
    /**
     * Typeahead over the canonical name **and** known aliases, so a user typing
     * "Southern Glazers" finds a party recorded as "Southern Glazer's Wine &
     * Spirits, LLC".
     */
    search: z.string().min(1).max(200).optional(),
    party_type: z.array(PartyTypeSchema).min(1).optional(),
    /** Omit to include only active parties. */
    include_inactive: z.boolean().optional(),
    /**
     * Include the alias list. Off by default — it is only needed by the
     * pseudonymisation path and the alias manager, not by a dropdown.
     */
    include_aliases: z.boolean().optional(),
    /** DR-05 — walk the corporate family. FR-11 depends on this walk. */
    include_hierarchy: z.boolean().optional(),
    /**
     * Which alias states to include when `include_aliases` is set. Default
     * `['active']` — the pseudonymisation path wants exactly what the map will
     * substitute. The Party aliases surface asks for `['pending']` to build its
     * approval queue and for all four to show one party's history (D-B8).
     */
    alias_state: z.array(PartyAliasStateSchema).min(1).optional(),
    /**
     * Return only parties that have at least one alias in `alias_state` — the
     * queue mode. With `alias_state: ['pending']` this is *"every party with a
     * leak-detector proposal waiting for a human"*, which is the §4A.5 loop's
     * work list.
     */
    require_alias_match: z.boolean().optional(),
    /**
     * CLM-114 · R-114-5 — include each party's vendor-list references (entity,
     * vendor number, address, its source and batch) for the Party aliases
     * page's "Vendor list" panel. Off by default.
     */
    include_references: z.boolean().optional(),
    page: PageRequestSchema.optional(),
})
    .strict();
/**
 * One resolved party.
 *
 * `party` is a discriminated union on `party_type`, which is what pins the TD-02
 * key prefix and the IR-02/IR-03 system of record together — a Vendor resolving to
 * Qfly is not representable.
 */
export const ResolvedPartySchema = z.object({
    party: PartySchema,
    /**
     * `null` unless `include_aliases` was set. Ordered longest-first, which is the
     * order substitution must apply them in: §4A.3 was learned from a live failure —
     * substituting a short form before a long one leaves fragments of the long one
     * behind.
     */
    aliases: z.array(PartyAliasSchema).nullable(),
    /**
     * The pseudonym token this party's name is replaced with before anything leaves
     * the tenant — e.g. `[CLIENT:SGW]`. Derived from the party key, so it is stable
     * and reversible in-tenant via the versioned map.
     */
    pseudonym_token: z.string().min(1).max(64),
    /** Corporate family, nearest ancestor first. `null` unless requested. */
    ancestors: z
        .array(z.object({
        party_key: PartyKeySchema,
        display_name: z.string().min(1).max(200),
    }))
        .nullable(),
    /**
     * Contract keys for this party **within the caller's scope**. A party may be
     * resolvable for selection while some of its contracts are not visible; that is
     * not a contradiction, it is NFR-06 working.
     */
    in_scope_contract_keys: z.array(ContractKeySchema),
    /**
     * CLM-114 · R-114-5 — `null` unless `include_references` was set; then the
     * party's vendor-list references (empty for a Client, a Partner, or a vendor
     * no list has named). Never an individual's reason or the left-out aliases
     * (R-113-5): those are returned only by the Admin import endpoints.
     */
    references: z.array(VendorReferenceSchema).nullable(),
});
export const ResolvePartyResponseSchema = z.object({
    parties: z.array(ResolvedPartySchema),
    page: PageInfoSchema,
    /**
     * Which system of record answered, and when it was last reconciled. DR-04 makes
     * Qfly and Dynamics authoritative for party master data; if the cached display
     * name here disagrees with them, they win. Surfaced so a stale cache is visible
     * rather than mistaken for a data-entry error.
     */
    sor_freshness: z.array(z.object({
        sor_system: SorSystemSchema,
        last_reconciled_utc: IsoTimestampUtcSchema.nullable(),
    })),
});
/**
 * Party-alias *management* — the gap this file once flagged — is closed by
 * `manage-party-alias` (`POST /v1/parties/aliases/actions`, D-B8, CLM-56): a
 * discriminated union of `propose | approve | reject | retire`, one write per
 * call, gated on `party:manage_alias` (admin and Legal), with the audit row
 * written by `tg_party_alias_audit`. See `manage-party-alias.ts`.
 *
 * Clause-*search* aliases remain a different table with different semantics and
 * are covered by `manage-clause`. Party aliases are an FR-29 control, and putting
 * them on a clause endpoint would be filing a compliance control under search
 * tuning — which is why the eighth endpoint exists rather than a ninth action.
 */
//# sourceMappingURL=resolve-party.js.map